import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import countryService from "../../services/country-service";
import cityService from "../../services/city-service";
import provinceService from "../../services/province-service";
import regionService from "../../services/region-service";
import {
  handleGetAdditionalServices,
  handleGetCities,
  handleGetCountries,
  handleGetCurrencies,
  handleGetCurrentRate,
  handleGetLanguages,
  handleGetOrderStates,
  handleGetOrderTypes,
  handleGetPaymentStatuses,
  handleGetPaymentTypes,
  handleGetProductCategories,
  handleGetProductStores,
  handleGetProvinces,
  handleGetRecipientDocumentTypes,
  handleGetRegions,
  handleGetShippingMethods,
  handleGetUserTypes,
  handleGetWarehouses,
  handleGetWeightUnits,
  handleRejection,
  OrderStateModel,
  ProductCategoryModel,
  ProductTypeModel,
  StaticDataState,
  StoreModel,
} from "../reducers/static-data.reducers";
import shippingMethodService from "../../services/shipping-method-service";
import weightUnitService from "../../services/weight-unit-service";
import currenciesService from "../../services/currencies-service";
import languagesService from "../../services/languages.service";
import paymentStatusService from "../../services/payment-status.service";
import { PaymentStatusModel } from "../../pages/shared/models/order/payment-status.model";
import { Language } from "../../pages/shared/models/language";
import stateTypeService from "../../services/state-type-service";
import paymentTypesService from "../../services/payment-types.service";
import { PaymentTypeModel } from "../../pages/shared/models/order/payment-type.model";
import { WarehouseModel } from "../../pages/shared/models/order/warehouse.model";
import warehousesService from "../../services/warehouses.service";
import additionalServicesService from "../../services/additional-service.service";
import productTypesService from "../../services/product-type.service";
import recipientDocumentTypesService from "../../services/recipient-document-type.service";
import { CountryModel } from "../../pages/shared/models/countryModel";
import { AdditionalServiceModel } from "../../pages/shared/models/order/additional-service.model";
import { RecipientDocumentTypeModel } from "../../pages/shared/models/user/recipient/recipient-document-type.model";
import productCategoriesService from "../../services/product-categories.service";
import userTypeService from "../../services/user-type.service";
import { UserTypeModel } from "../../pages/shared/models/user/user-type.model";
import { CityModel } from "../../pages/shared/models/cityModel";
import { ProvinceModel } from "../../pages/shared/models/provinceModel";
import { RegionModel } from "../../pages/shared/models/regionModel";
import productStoresService from "../../services/product-stores.service";
import rateService from "../../services/rate-service";
import { RateResponseModel } from "../../pages/shared/models/api/rate-response.model";

export const getOrderTypes = createAsyncThunk(
  "getOrderTypes",
  async () => {
    const result = await stateTypeService.getOrderTypes();
    const orderTypes = result?.data?.orderTypes;
    return { orderTypes };
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.orderTypes && state.orderTypes.length);
    },
  },
);

export const getOrderStates = createAsyncThunk(
  "getOrderStates",
  async () => {
    const result = await stateTypeService.getOrderStates();
    const orderStates = result?.data?.orderStates;
    return { orderStates };
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.orderStates && state.orderStates.length);
    },
  },
);

export const getCountries = createAsyncThunk(
  "getCountries",
  async (forceLoad?: boolean) => {
    const result = await countryService.getCountries();
    const countries = result?.data?.countries;
    return { countries };
  },
);

export const getProvinces = createAsyncThunk(
  "getProvinces",
  async (cityId: number | undefined) => {
    return await provinceService.getProvinces({ cityId });
  },
);

export const getCities = createAsyncThunk(
  "getCities",
  async (countryId: number | undefined) => {
    return await cityService.getCities({ countryId });
  },
);

export const getRegions = createAsyncThunk(
  "getRegions",
  async (cityId: number | undefined) => {
    return await regionService.getRegions({ cityId });
  },
);

export const getShippingMethods = createAsyncThunk(
  "getShippingMethods",
  async () => {
    const shippingMethods = await shippingMethodService.getShippingMethods();
    return { shippingMethods: shippingMethods?.data?.shippingMethods };
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.shippingMethods && state.shippingMethods.length);
    },
  },
);

export const getWeightUnits = createAsyncThunk(
  "getWeightUnits",
  async () => {
    const result = await weightUnitService.getWeightUnits();
    let weightUnits = result?.data?.weightUnits;
    weightUnits = weightUnits.map((unit: any) => {
      unit.messageKey = unit.name;
      return unit;
    });
    return { weightUnits };
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.weightUnits && state.weightUnits.length);
    },
  },
);

export const getLanguages = createAsyncThunk(
  "getLanguages",
  async () => {
    const result = await languagesService.getLanguages();
    const languages = result?.data?.languages;
    return { languages };
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.languages && state.languages.length);
    },
  },
);

export const getCurrencies = createAsyncThunk(
  "getCurrencies",
  async () => {
    const result = await currenciesService.getCurrencies();
    const currencies = result?.data?.currencies;

    return { currencies };
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.currencies && state.currencies.length);
    },
  },
);

export const getPaymentStatuses = createAsyncThunk(
  "getPaymentStatuses",
  async () => {
    const result = await paymentStatusService.getPaymentStatuses();
    return result?.data;
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.paymentStatuses && state.paymentStatuses.length);
    },
  },
);

export const getPaymentTypes = createAsyncThunk(
  "getPaymentTypes",
  async () => {
    const result = await paymentTypesService.getPaymentTypes();
    return result?.data;
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.paymentTypes && state.paymentTypes.length);
    },
  },
);

export const getWarehouses = createAsyncThunk(
  "getWarehouses",
  async (forceLoad?: boolean) => {
    const result = await warehousesService.getWarehouses();
    return result?.warehouses;
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.warehouses && state.warehouses.length);
    },
  },
);

export const getAdditionalServices = createAsyncThunk(
  "getAdditionalServices",
  async () => {
    const result = await additionalServicesService.getServices();
    return result?.data;
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.additionalServices && state.additionalServices.length);
    },
  },
);

export const getUserTypes = createAsyncThunk(
  "getUserTypes",
  async () => {
    const result = await userTypeService.getUserTypes();
    return result?.data;
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.userTypes && state.userTypes.length);
    },
  },
);

export const getRecipientDocumentTypes = createAsyncThunk(
  "getRecipientDocumentTypes",
  async () => {
    const result =
      await recipientDocumentTypesService.getRecipientDocumentTypes();
    return result?.data;
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(
        state.recipientDocumentTypes && state.recipientDocumentTypes.length
      );
    },
  },
);

export const getProductTypes = createAsyncThunk(
  "getProductTypes",
  async () => {
    const result = await productTypesService.getProductTypes();
    return result?.data;
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.productTypes && state.productTypes.length);
    },
  },
);

export const getProductCategories = createAsyncThunk(
  "getProductCategories",
  async () => {
    const result = await productCategoriesService.getProductCategories({});
    return result?.data;
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.productCategories && state.productCategories.length);
    },
  },
);

export const getProductStores = createAsyncThunk(
  "getProductStores",
  async ({limit, page, filter}: {limit?: number, page?: number, filter?: Record<string, any>}) => {
    return await productStoresService.getProductStores(filter, limit, page);
  },
  {
    condition(thunkData, { getState }): boolean {
      const state = (getState() as any).staticDataReducer as StaticDataState;
      return !(state.productStores && state.productStores.length);
    },
  },
);

export const getCurrentRate = createAsyncThunk("getCurrentRate", async () => {
  return await rateService.getRate();
});

export const staticDataSlice = createSlice({
  name: "staticData",
  initialState: {
    orderTypes: [],
    orderStates: [] as OrderStateModel[],
    countries: [] as CountryModel[],
    provinces: [] as ProvinceModel[],
    cities: [] as CityModel[],
    regions: [] as RegionModel[],
    shippingMethods: [],
    weightUnits: [],
    currencies: [],
    languages: [] as Language[],
    paymentStatuses: [] as PaymentStatusModel[],
    paymentTypes: [] as PaymentTypeModel[],
    warehouses: [] as WarehouseModel[],
    additionalServices: [] as AdditionalServiceModel[],
    userTypes: [] as UserTypeModel[],
    recipientDocumentTypes: [] as RecipientDocumentTypeModel[],
    productTypes: [] as ProductTypeModel[],
    productCategories: [] as ProductCategoryModel[],
    productStores: [] as StoreModel[],
    currentRate: {} as RateResponseModel,
    inWarehouseStateId: 4,
  } as StaticDataState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getOrderTypes.fulfilled, (state, action) =>
      handleGetOrderTypes(state, action.payload),
    );
    builder.addCase(getOrderStates.fulfilled, (state, action) =>
      handleGetOrderStates(state, action.payload),
    );
    builder.addCase(getCountries.fulfilled, (state, action) =>
      handleGetCountries(state, action.payload),
    );
    builder.addCase(getProvinces.fulfilled, (state, action) =>
      handleGetProvinces(state, action.payload),
    );
    builder.addCase(getCities.fulfilled, (state, action) =>
      handleGetCities(state, action.payload),
    );
    builder.addCase(getRegions.fulfilled, (state, action) =>
      handleGetRegions(state, action.payload),
    );
    builder.addCase(getShippingMethods.fulfilled, (state, action) =>
      handleGetShippingMethods(state, action.payload),
    );
    builder.addCase(getWeightUnits.fulfilled, (state, action) =>
      handleGetWeightUnits(state, action.payload),
    );
    builder.addCase(getCurrencies.fulfilled, (state, action) =>
      handleGetCurrencies(state, action.payload),
    );
    builder.addCase(getCurrencies.rejected, (state, action) =>
      handleGetCurrencies(state, action.payload),
    );
    builder.addCase(getLanguages.fulfilled, (state, action) =>
      handleGetLanguages(state, action.payload),
    );
    builder.addCase(getPaymentStatuses.fulfilled, (state, action) =>
      handleGetPaymentStatuses(state, action.payload),
    );
    builder.addCase(getPaymentTypes.fulfilled, (state, action) =>
      handleGetPaymentTypes(state, action.payload),
    );
    builder.addCase(getWarehouses.fulfilled, (state, action) =>
      handleGetWarehouses(state, action.payload),
    );
    builder.addCase(getAdditionalServices.fulfilled, (state, action) =>
      handleGetAdditionalServices(state, action.payload),
    );
    builder.addCase(getRecipientDocumentTypes.fulfilled, (state, action) =>
      handleGetRecipientDocumentTypes(state, action.payload),
    );
    builder.addCase(getUserTypes.fulfilled, (state, action) =>
      handleGetUserTypes(state, action.payload),
    );
    builder.addCase(getProductCategories.fulfilled, (state, action) =>
      handleGetProductCategories(state, action.payload),
    );
    builder.addCase(getProductStores.fulfilled, (state, action) =>
      handleGetProductStores(state, action.payload),
    );
    builder.addCase(getCurrentRate.fulfilled, (state, action) =>
      handleGetCurrentRate(state, action.payload),
    );

    builder.addCase(getOrderStates.rejected, (state, action) =>
      handleRejection(state, action.payload),
    ); // TODO: for test, should be removed
  },
});

export default staticDataSlice.reducer;
